import cloneDeep from "lodash/cloneDeep";
import sortBy from "lodash/sortBy";

enum GeneticOptimize {
  Maximize = 0,
  Minimize = 1,
}

enum Select1 {
  Tournament2 = 0,
  Tournament3 = 1,
  Fittest = 2,
  Random = 3,
  RandomLinearRank = 4,
  Sequential = 5,
}

export interface GeneticConfiguration {
  size: number;
  crossover: number;
  mutation: number;
  iterations: number;
  fittestAlwaysSurvives: boolean;
  maxResults: number;
  skip: number;
}

export type Population<EntityType> = Array<{
  fitness: number;
  entity: EntityType;
}>;

export type Stats = {
  maximum?: number;
  mean?: number;
  minimum?: number;
  stdev?: number;
};

export type GenerationCallback<EntityType> = (
  pop: Population<EntityType>,
  generation: number,
  stats: Stats,
) => boolean;

export type NotificationCallback<EntityType> = (
  pop: Population<EntityType>,
  generation: number,
  stats: Stats,
  isFinished: boolean,
) => boolean;

export type FitnessFunction<EntityType> = (type: EntityType) => Promise<number>;
export type MutateFunction<EntityType> = (type: EntityType) => EntityType;

export class Genetic<EntityType = unknown> {
  private fitness: FitnessFunction<EntityType>;
  private seed: () => EntityType;
  private mutate: MutateFunction<EntityType>;
  private select1: Select1;
  private optimize: GeneticOptimize;
  private notification: NotificationCallback<EntityType>;
  private generation?: GenerationCallback<EntityType>;
  private crossover?: (
    parentA: EntityType,
    parentB: EntityType,
  ) => [EntityType, EntityType];
  private configuration: GeneticConfiguration;
  private internalGenState: {
    rlr: number;
    seq: number;
  };
  private entities: EntityType[];
  // population
  constructor(
    fitness: FitnessFunction<EntityType>,
    seed: () => EntityType,
    mutate: MutateFunction<EntityType>,
    notification: NotificationCallback<EntityType>,
    generation?: GenerationCallback<EntityType>,
    crossover?: (
      parentA: EntityType,
      parentB: EntityType,
    ) => [EntityType, EntityType],
    optimize: GeneticOptimize = GeneticOptimize.Minimize,
    select1: Select1 = Select1.Tournament2,
  ) {
    this.fitness = fitness;
    this.seed = seed;
    this.mutate = mutate;
    this.crossover = crossover;
    this.select1 = select1;
    this.optimize = optimize;
    this.generation = generation;
    this.notification = notification;
    this.configuration = {
      crossover: 0,
      fittestAlwaysSurvives: true,
      iterations: 10,
      maxResults: 4,
      mutation: 0.2,
      size: 10,
      skip: 0,
    };

    this.internalGenState = {
      rlr: 0,
      seq: 0,
    };

    this.entities = [];
  }
  public async start() {
    const mutateOrNot = (entity: EntityType): EntityType => {
      // applies mutation based on mutation probability
      return Math.random() <= this.configuration.mutation && this.mutate
        ? this.mutate(cloneDeep(entity))
        : entity;
    };

    // seed the population
    for (let i = 0; i < this.configuration.size; ++i) {
      this.entities.push(cloneDeep(this.seed()));
    }

    for (let i = 0; i < this.configuration.iterations; ++i) {
      // reset for each generation
      this.internalGenState = {
        rlr: 0,
        seq: 0,
      };

      // score and sort
      let pop: Array<{ fitness: number; entity: EntityType }> = [];
      for (const entity of this.entities) {
        pop.push({ fitness: await this.fitness(entity), entity });
      }
      pop = sortBy(pop, "fitness");
      if (this.optimize === GeneticOptimize.Maximize) {
        pop = pop.reverse();
      }

      // generation notification
      const mean = pop.reduce((a, b) => a + b.fitness, 0) / pop.length;
      const stdev = Math.sqrt(
        pop
          .map((a) => (a.fitness - mean) * (a.fitness - mean))
          .reduce((a, b) => a + b, 0) / pop.length,
      );

      const stats = {
        maximum: pop[0]?.fitness,
        mean,
        minimum: pop[pop.length - 1]?.fitness,
        stdev,
      };

      const r = this.generation
        ? this.generation(pop.slice(0, this.configuration.maxResults), i, stats)
        : true;
      const isFinished =
        (typeof r !== "undefined" && !r) ||
        i === this.configuration.iterations - 1;

      if (
        this.notification &&
        (isFinished ||
          this.configuration.skip === 0 ||
          i % this.configuration.skip === 0)
      ) {
        this.notification(
          pop.slice(0, this.configuration.maxResults),
          i,
          stats,
          isFinished,
        );
      }

      if (isFinished) {
        break;
      }

      // crossover and mutate
      const newPop: EntityType[] = [];

      if (this.configuration.fittestAlwaysSurvives) {
        // lets the best solution fall through
        if (!pop[0]) {
          throw new Error("No best solution found");
        }
        newPop.push(pop[0].entity);
      }

      while (newPop.length < this.configuration.size) {
        if (
          this.crossover && // if there is a crossover function
          Math.random() <= this.configuration.crossover && // base crossover on specified probability
          newPop.length + 1 < this.configuration.size // keeps us from going 1 over the max population size
        ) {
          const parents = this.Select2(pop);
          if (!parents[0] || !parents[1]) {
            throw new Error("No parents found");
          }
          const children: EntityType[] = this.crossover(
            cloneDeep(parents[0]),
            cloneDeep(parents[1]),
          ).map(mutateOrNot);
          if (!children[0] || !children[1]) {
            throw new Error("No children found");
          }
          newPop.push(children[0], children[1]);
        } else {
          newPop.push(mutateOrNot(this.Select1(pop)));
        }
      }

      this.entities = newPop;
    }
  }
  public async evolve(config: GeneticConfiguration) {
    this.configuration = config;
    await this.start();
  }
  private Select1(pop: Population<EntityType>): EntityType {
    switch (this.select1) {
      case Select1.Tournament2: {
        const n = pop.length;
        const a = pop[Math.floor(Math.random() * n)];
        const b = pop[Math.floor(Math.random() * n)];
        if (!a || !b) {
          throw new Error("No parents found");
        }
        return (this.optimize === GeneticOptimize.Maximize) ===
          a.fitness > b.fitness
          ? a.entity
          : b.entity;
      }
      case Select1.Tournament3: {
        const n = pop.length;
        const a = pop[Math.floor(Math.random() * n)];
        const b = pop[Math.floor(Math.random() * n)];
        const c = pop[Math.floor(Math.random() * n)];
        if (!a || !b || !c) {
          throw new Error("No parents found");
        }
        if (this.optimize === GeneticOptimize.Maximize) {
          let best = a.fitness > b.fitness ? a : b;
          best = best.fitness > c.fitness ? best : c;
          return best.entity;
        } else {
          let best = a.fitness < b.fitness ? a : b;
          best = best.fitness < c.fitness ? best : c;
          return best.entity;
        }
      }
      case Select1.Fittest: {
        if (!pop[0]) {
          throw new Error("No best solution found");
        }
        return pop[0].entity;
      }
      case Select1.Random: {
        const index = Math.floor(Math.random() * pop.length);
        if (!pop[index]) {
          throw new Error("Invalid index");
        }
        return pop[index].entity;
      }
      case Select1.RandomLinearRank: {
        this.internalGenState.rlr = this.internalGenState.rlr || 0;
        const index = Math.floor(
          Math.random() * Math.min(pop.length, this.internalGenState.rlr++),
        );
        if (!pop[index]) {
          throw new Error("Invalid index");
        }
        return pop[index].entity;
      }
      case Select1.Sequential: {
        this.internalGenState.seq = this.internalGenState.seq || 0;
        const index = this.internalGenState.seq++ % pop.length;
        if (!pop[index]) {
          throw new Error("Invalid index");
        }
        return pop[index].entity;
      }
    }
  }
  private Select2(pop: Population<EntityType>): EntityType[] {
    return [this.Select1(pop), this.Select1(pop)];
  }
}
